import { Loader } from '@myosh/odin-components';
import { cloneDeep } from 'lodash-es';
import { createContext, useContext, useRef } from 'react';
import { useNavigate } from 'react-router';
import type { BowtieConfiguration } from '../@types/common-data-types';
import { useAppConfigurationQuery } from '../api/enhanced/enhanced-v4-api';
import ErrorPage from '../components/common/error-page.component';

interface ConfigurationContextProps {
  children: React.ReactNode;
}

const ConfigurationContext = createContext<BowtieConfiguration | undefined>(undefined);

// Provides all information needed for Bowtie
export const ConfigurationProvider: React.FC<ConfigurationContextProps> = ({ children }: ConfigurationContextProps) => {
  const navigate = useNavigate();

  const loading = useRef(true);
  const error = useRef(false);
  const configuration = useRef<BowtieConfiguration | undefined>(undefined);

  const { data, isFetching, isError } = useAppConfigurationQuery();

  if (isError) {
    error.current = true;
    loading.current = false;
  } else if (data && !isFetching) {
    const config = cloneDeep(data);

    configuration.current = config;
    error.current = false;
    loading.current = false;
  }

  if (error.current) {
    return (
      <ErrorPage
        message="Unable to load the bowtie configuration. Please check your configuration or reload the page to try again."
        actionLabel="Reload"
        onActionClick={() => navigate(0)}
      />
    );
  } else if (loading.current) {
    return (
      <div
        data-cy="configuration-loading"
        className="mx-auto my-0 flex h-screen w-full flex-col items-center justify-center"
      >
        <Loader title={'Loading diagram configuration'} />
      </div>
    );
  } else {
    return <ConfigurationContext.Provider value={configuration.current}>{children}</ConfigurationContext.Provider>;
  }
};

// context consumer hook
export const useConfigurationContext = () => {
  const context = useContext(ConfigurationContext);

  if (context === undefined) {
    throw new Error('useConfigurationContext must be used within ConfigurationProvider');
  }

  return context;
};
